import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import React, { type ElementType, useMemo } from 'react';

import {
  CommunicationIcon,
  DeleteIcon,
  DocumentIcon,
  EditIcon,
  OtherResourceIcon,
  PhoneIcon,
  ReminderIcon,
} from '../../../icons';
import type { TagColor } from '../../atoms/Tag';
import type { ResourceMenuItem } from '../../organisms/ResourceMenuActions';

export type CommunicationType = 'email' | 'reminder' | 'letter' | 'call' | 'other' | string;

export type CommunicationDocument = {
  id?: string | null;
  name: string;
  type?: string | null;
  url?: string | null;
  file_url?: string | null;
};

export type CommunicationItem = {
  id: string;
  type: CommunicationType;
  title?: string | null;
  comment?: string | null;
  date?: string | Date | null;
  providerName?: string | null;
  provider_name?: string | null;
  contactName?: string | null;
  contact_name?: string | null;
  documents?: CommunicationDocument[] | null;
};

export type CommunicationCardAction = ResourceMenuItem;

export type CommunicationCardProps = {
  item: CommunicationItem;
  documents?: CommunicationDocument[];
  maxCommentLines?: number;
  onEdit?: (item: CommunicationItem) => void;
  onDelete?: (item: CommunicationItem) => void;
  action?: React.ReactNode;
  actions?: (CommunicationCardAction | null | undefined | false)[];
  actionsButtonTooltip?: string;
  actionsButtonAriaLabel?: string;
  'data-testid'?: string;
};

type TypeMeta = {
  label: string;
  tagColor: TagColor;
  icon: ElementType;
  // "Autre" is not an exchange: no type tag (the title says what the action was),
  // and its people read as contact / provider rather than sender / recipient.
  isExchange: boolean;
};

export function getTypeMeta(type: CommunicationType): TypeMeta {
  switch (type) {
    case 'email':
      return { label: 'Email', tagColor: 'communications', icon: CommunicationIcon, isExchange: true };
    case 'reminder':
      return { label: 'Relance', tagColor: 'rappel', icon: ReminderIcon, isExchange: true };
    case 'letter':
      return { label: 'Courrier', tagColor: 'documents', icon: DocumentIcon, isExchange: true };
    case 'call':
      return { label: 'Appel', tagColor: 'providers', icon: PhoneIcon, isExchange: true };
    case 'other':
    default:
      return { label: 'Autre', tagColor: 'autre', icon: OtherResourceIcon, isExchange: false };
  }
}

export function formatCommunicationDate(rawDate: CommunicationItem['date']): string | null {
  if (!rawDate) return null;
  const dateObj = typeof rawDate === 'string' ? new Date(rawDate) : rawDate;
  if (Number.isNaN(dateObj.getTime())) return null;
  return format(dateObj, "d MMMM yyyy 'à' HH'h'mm", { locale: fr });
}

export function getCommunicationPeople(item: CommunicationItem) {
  return {
    providerName: item.providerName?.trim() || item.provider_name?.trim() || null,
    contactName: item.contactName?.trim() || item.contact_name?.trim() || null,
  };
}

export function getCommunicationDocuments(
  documents: CommunicationDocument[] | null | undefined,
): CommunicationDocument[] {
  return (documents ?? []).filter((doc): doc is CommunicationDocument => Boolean(doc && doc.name));
}

export function useCommunicationCard(props: CommunicationCardProps) {
  const typeMeta = getTypeMeta(props.item.type);

  const rawDate = props.item.date;
  const formattedDate = useMemo(() => formatCommunicationDate(rawDate), [rawDate]);
  const people = getCommunicationPeople(props.item);

  const documents = useMemo(
    () => getCommunicationDocuments(props.documents ?? props.item.documents),
    [props.documents, props.item.documents],
  );

  function handleEdit() {
    props.onEdit?.(props.item);
  }

  function handleDelete() {
    props.onDelete?.(props.item);
  }

  const actions = useMemo(() => {
    const customActions = (props.actions ?? []).filter((action): action is CommunicationCardAction => Boolean(action));
    const hasCustomEdit = customActions.some((a) => 'id' in a && a.id === 'edit');
    const hasCustomDelete = customActions.some((a) => 'id' in a && a.id === 'delete');

    const result: CommunicationCardAction[] = [];

    if (props.onEdit && !hasCustomEdit) {
      result.push({
        id: 'edit',
        label: 'Modifier',
        icon: React.createElement(EditIcon, { fontSize: 'small' }),
        onClick: handleEdit,
      });
    }

    result.push(...customActions);

    if (props.onDelete && !hasCustomDelete) {
      result.push({
        id: 'delete',
        label: 'Supprimer',
        icon: React.createElement(DeleteIcon, { fontSize: 'small' }),
        onClick: handleDelete,
      });
    }

    return result;
  }, [props.actions, props.onEdit, props.onDelete, props.item]);

  const hasActions = actions.length > 0;
  const actionsButtonTooltip = props.actionsButtonTooltip ?? 'Options de la communication';
  const actionsButtonAriaLabel = props.actionsButtonAriaLabel ?? actionsButtonTooltip;

  return {
    typeLabel: typeMeta.label,
    tagColor: typeMeta.tagColor,
    IconComponent: typeMeta.icon,
    isExchange: typeMeta.isExchange,
    formattedDate,
    providerName: people.providerName,
    contactName: people.contactName,
    documents,
    hasDocuments: documents.length > 0,
    actions,
    hasActions,
    actionsButtonTooltip,
    actionsButtonAriaLabel,
    handleEdit,
    handleDelete,
  };
}
